import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Building2, User, MapPin, DollarSign } from 'lucide-react';
import ActivityTimeline from '../components/ActivityTimeline';
import FileAttachments from '../components/FileAttachments';

const LeadDetailPage = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [leadScore, setLeadScore] = useState(null);
  const [company, setCompany] = useState(null);
  const [contact, setContact] = useState(null);

  useEffect(() => {
    fetchLead();
    fetchLeadScore();
  }, [leadId]);

  const fetchLead = async () => {
    try {
      const response = await api.get(`/leads/${leadId}`);
      setLead(response.data);
      
      // Fetch related company and contact
      if (response.data.company_id) {
        const companyRes = await api.get(`/companies/${response.data.company_id}`);
        setCompany(companyRes.data);
      }
      if (response.data.contact_id) {
        const contactRes = await api.get(`/contacts/${response.data.contact_id}`);
        setContact(contactRes.data);
      }
    } catch (error) {
      console.error('Error fetching lead:', error);
      toast.error('Failed to load lead details');
    }
  };

  const fetchLeadScore = async () => {
    try {
      const response = await api.get(`/leads/${leadId}/score`);
      setLeadScore(response.data);
    } catch (error) {
      console.error('Error fetching lead score:', error);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 75) return 'bg-green-100 text-green-800';
    if (score >= 50) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const getStatusColor = (status) => {
    const colors = {
      New: 'bg-gray-100 text-gray-800',
      Contacted: 'bg-blue-100 text-blue-800',
      Qualified: 'bg-purple-100 text-purple-800',
      QuoteSent: 'bg-yellow-100 text-yellow-800',
      Negotiation: 'bg-orange-100 text-orange-800',
      Won: 'bg-green-100 text-green-800',
      Lost: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (!lead) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="outline" size="sm" onClick={() => navigate('/leads')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Leads
        </Button>
      </div>

      {/* Lead Overview */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="text-2xl">{lead.potential_project_name}</CardTitle>
              <p className="text-slate-600 mt-1">Lead Details</p>
            </div>
            <div className="flex gap-2">
              <Badge className={getStatusColor(lead.status)}>{lead.status}</Badge>
              {leadScore && (
                <Badge className={getScoreColor(leadScore.score)}>
                  Score: {leadScore.score}
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Lead Info */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm text-slate-900">Lead Information</h3>
              
              {lead.location && (
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-slate-700">Location</p>
                    <p className="text-sm text-slate-600">{lead.location}</p>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-3">
                <DollarSign className="h-5 w-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-slate-700">Estimated Value</p>
                  <p className="text-sm text-slate-600">
                    ${lead.est_value ? lead.est_value.toLocaleString() : 'N/A'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {lead.est_units && (
                  <div>
                    <p className="text-sm font-medium text-slate-700">Units</p>
                    <p className="text-sm text-slate-600">{lead.est_units}</p>
                  </div>
                )}
                {lead.est_sqft && (
                  <div>
                    <p className="text-sm font-medium text-slate-700">Square Feet</p>
                    <p className="text-sm text-slate-600">{lead.est_sqft.toLocaleString()}</p>
                  </div>
                )}
              </div>

              <div>
                <p className="text-sm font-medium text-slate-700">Source</p>
                <Badge variant="outline" className="mt-1">{lead.source}</Badge>
              </div>
            </div>

            {/* Related Info */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm text-slate-900">Related Contacts</h3>
              
              {company && (
                <div className="flex items-start gap-3">
                  <Building2 className="h-5 w-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-slate-700">Company</p>
                    <p className="text-sm text-slate-600">{company.name}</p>
                  </div>
                </div>
              )}

              {contact && (
                <div className="flex items-start gap-3">
                  <User className="h-5 w-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-slate-700">Contact</p>
                    <p className="text-sm text-slate-600">
                      {contact.first_name} {contact.last_name}
                    </p>
                    {contact.email && (
                      <p className="text-xs text-slate-500">{contact.email}</p>
                    )}
                  </div>
                </div>
              )}

              {lead.notes && (
                <div>
                  <p className="text-sm font-medium text-slate-700">Notes</p>
                  <p className="text-sm text-slate-600 mt-1">{lead.notes}</p>
                </div>
              )}

              {leadScore && (
                <div>
                  <p className="text-sm font-medium text-slate-700">Score Breakdown</p>
                  <div className="mt-2 space-y-1">
                    {Object.entries(leadScore.factors).map(([key, value]) => (
                      <div key={key} className="flex justify-between text-xs">
                        <span className="text-slate-600 capitalize">{key}</span>
                        <span className="font-medium text-slate-900">{value} pts</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Activity Timeline and Files */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ActivityTimeline entityType="Lead" entityId={leadId} />
        <FileAttachments entityType="Lead" entityId={leadId} />
      </div>
    </div>
  );
};

export default LeadDetailPage;
